import { Highlighter, PenLine } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlays";
import { cn } from "@/lib/utils";

import {
  ADAPTIVE_INK,
  FREEDRAW_SIZE_FACTOR,
  previewDotPx,
  resolveInk,
  widthsFor,
  type InkSetting,
  type InkTool,
} from "./ink-settings";

/** The writing rail — the pen/highlighter layer over Excalidraw's own toolbar.
 *
 * Excalidraw keeps its icons, its number-key tool selection, and its shapes;
 * this rail adds what a WRITER needs and a diagramming tool doesn't: a pen and a
 * highlighter that each remember their ink, a width ladder finer than
 * Excalidraw's three presets, and three quick colours under the thumb.
 *
 * LAYOUT STABILITY IS A HARD INVARIANT (the thing that made the old toolbar
 * unusable): every slot is rendered unconditionally at a fixed size, and the
 * active state is a ring + tint — never a border-width, padding, or content
 * change. Switching tools, opening a picker, or landing on the eraser therefore
 * cannot move a single control sideways. Nothing here is conditional markup.
 *
 * Widths preview as DOTS at their true on-paper diameter — a round nib's width
 * IS a circle, so the affordance is the thing itself rather than an abstract
 * wedge you have to decode.
 */

/** Fixed geometry — quoted rather than derived so the invariant is greppable. */
const SLOT = "h-10 w-10 shrink-0 grid place-items-center rounded-full transition-colors";

/** The rail and its pickers follow the PAPER, not the app shell — exactly like
 * the Excalidraw islands they sit beside (the surface themes those from the
 * paper too). A dark rail over a white sheet renders the pen icon and the
 * near-black ink swatch invisible, which is precisely what they exist to show.
 * Colours are literal rather than theme tokens because the app shell's theme is
 * the wrong input here. */
function railChrome(paper: "light" | "dark") {
  return paper === "dark"
    ? {
        surface: "border-white/10 bg-[#232329]/95 text-[#e3e3e8]",
        panel: "border-white/10 bg-[#232329] text-[#e3e3e8]",
        hover: "hover:bg-white/10",
        divider: "bg-white/15",
        ring: "ring-white/25",
      }
    : {
        surface: "border-black/10 bg-white/95 text-[#1b1b1f]",
        panel: "border-black/10 bg-white text-[#1b1b1f]",
        hover: "hover:bg-black/5",
        divider: "bg-black/10",
        ring: "ring-black/15",
      };
}

type Chrome = ReturnType<typeof railChrome>;

function Slot({
  active,
  onClick,
  label,
  children,
  testId,
  chrome,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  testId?: string;
  chrome: Chrome;
}) {
  return (
    <Tip label={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        data-testid={testId}
        onClick={onClick}
        className={cn(SLOT, active ? "bg-primary/20 ring-1 ring-primary" : chrome.hover)}
      >
        {children}
      </button>
    </Tip>
  );
}

/** A filled circle at a width's real on-paper diameter. */
function WidthDot({ width, color }: { width: number; color: string }) {
  const d = previewDotPx(width);
  return (
    <span
      className="block rounded-full"
      style={{ width: d, height: d, background: color }}
      aria-hidden
    />
  );
}

/** A section label inside a picker — inherits the panel's text colour so it
 * stays legible on either paper. */
function PickerLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[10px] uppercase tracking-wide opacity-60">{children}</div>
  );
}

/** The palette offered when customizing a slot — Excalidraw's own ink colours,
 * so a colour picked here matches one picked in its properties panel. */
const SWATCHES = [
  "#1e1e1e",
  "#e03131",
  "#2f9e44",
  "#1971c2",
  "#f08c00",
  "#9c36b5",
  "#0c8599",
  "#e8590c",
  "#ffd43b",
  "#f783ac",
  "#868e96",
  "#ffffff",
];

export function WritingRail({
  tool,
  ink,
  slots,
  paper,
  writing,
  onPickTool,
  onPickWidth,
  onPickColor,
  onCustomizeSlot,
}: {
  /** Which writing tool the rail is set to (independent of Excalidraw's active
   * tool — picking a shape doesn't forget which pen was your last pen). */
  tool: InkTool;
  ink: Record<InkTool, InkSetting>;
  slots: string[];
  /** The paper's lightness — resolves adaptive ink, and themes the rail. */
  paper: "light" | "dark";
  /** True when Excalidraw is actually on the freedraw tool, i.e. the rail's
   * tool is the live one. Drives the active ring only — never the layout. */
  writing: boolean;
  onPickTool: (tool: InkTool) => void;
  onPickWidth: (width: number) => void;
  onPickColor: (color: string) => void;
  onCustomizeSlot: (index: number, color: string) => void;
}) {
  const current = ink[tool];
  const currentColor = resolveInk(current.color, paper);
  const ladder = widthsFor(tool);
  const chrome = railChrome(paper);

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-16 z-10 flex justify-center px-2">
      <div
        data-testid="writing-rail"
        className={cn(
          "pointer-events-auto flex items-center gap-1 rounded-full border px-2 py-1.5 shadow-md backdrop-blur",
          chrome.surface,
        )}
      >
        <Slot
          active={writing && tool === "pen"}
          onClick={() => onPickTool("pen")}
          label="Pen"
          testId="rail-pen"
          chrome={chrome}
        >
          <PenLine
            className="h-[18px] w-[18px]"
            style={{ color: resolveInk(ink.pen.color, paper) }}
          />
        </Slot>
        <Slot
          active={writing && tool === "highlighter"}
          onClick={() => onPickTool("highlighter")}
          label="Highlighter"
          testId="rail-highlighter"
          chrome={chrome}
        >
          <Highlighter
            className="h-[18px] w-[18px]"
            style={{ color: resolveInk(ink.highlighter.color, paper) }}
          />
        </Slot>

        <span className={cn("mx-0.5 h-6 w-px shrink-0", chrome.divider)} aria-hidden />

        {/* Width — the trigger IS a dot at the current width. */}
        <Popover>
          <Tip label={`${tool === "pen" ? "Pen" : "Highlighter"} width`}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Stroke width"
                data-testid="rail-width"
                className={cn(SLOT, "ring-1", chrome.ring, chrome.hover)}
              >
                <WidthDot width={current.width} color={currentColor} />
              </button>
            </PopoverTrigger>
          </Tip>
          <PopoverContent align="center" side="top" className={cn("w-auto", chrome.panel)}>
            <PickerLabel>Width</PickerLabel>
            <div className="flex items-center gap-1">
              {ladder.map((w) => (
                <Tip key={w} label={`${Math.round(w * FREEDRAW_SIZE_FACTOR * 10) / 10} px`}>
                  <button
                    type="button"
                    aria-label={`Width ${w}`}
                    aria-pressed={Math.abs(w - current.width) < 1e-6}
                    onClick={() => onPickWidth(w)}
                    className={cn(
                      SLOT,
                      Math.abs(w - current.width) < 1e-6
                        ? "bg-primary/20 ring-1 ring-primary"
                        : chrome.hover,
                    )}
                  >
                    <WidthDot width={w} color={currentColor} />
                  </button>
                </Tip>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <span className={cn("mx-0.5 h-6 w-px shrink-0", chrome.divider)} aria-hidden />

        {/* Three quick colours. Tapping the ACTIVE one opens its editor, so
            customizing costs no extra chrome on the rail. */}
        {slots.map((slotColor, i) => (
          <ColorSlot
            key={i}
            index={i}
            color={slotColor}
            shown={resolveInk(slotColor, paper)}
            selected={slotColor === current.color}
            chrome={chrome}
            onSelect={() => onPickColor(slotColor)}
            onCustomize={(c) => onCustomizeSlot(i, c)}
          />
        ))}
      </div>
    </div>
  );
}

function ColorSlot({
  index,
  color,
  shown,
  selected,
  chrome,
  onSelect,
  onCustomize,
}: {
  index: number;
  color: string;
  shown: string;
  selected: boolean;
  chrome: Chrome;
  onSelect: () => void;
  onCustomize: (color: string) => void;
}) {
  const label =
    color === ADAPTIVE_INK ? "Adaptive ink — black on light paper, white on dark" : color;
  return (
    <Popover>
      <Tip label={selected ? `${label} · tap again to change` : label}>
        <PopoverTrigger
          asChild
          onClick={(e) => {
            // A tap on an UNSELECTED slot just selects it; only the already-
            // selected slot opens its editor. One control, two intents, no
            // extra affordance on the rail.
            if (!selected) {
              e.preventDefault();
              onSelect();
            }
          }}
        >
          <button
            type="button"
            aria-label={`Colour ${index + 1}: ${label}`}
            aria-pressed={selected}
            data-testid={`rail-color-${index}`}
            className={cn(SLOT, selected ? "bg-primary/20 ring-1 ring-primary" : chrome.hover)}
          >
            <span
              className={cn("block h-5 w-5 rounded-full ring-1", chrome.ring)}
              style={{ background: shown }}
              aria-hidden
            />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="center" side="top" className={cn("w-auto", chrome.panel)}>
        <PickerLabel>Colour {index + 1}</PickerLabel>
        <div className="grid w-[168px] grid-cols-6 gap-1.5">
          {SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={c}
              onClick={() => onCustomize(c)}
              className={cn(
                "h-6 w-6 rounded-full ring-1 transition-transform hover:scale-110",
                c === color ? "ring-2 ring-primary" : chrome.ring,
              )}
              style={{ background: c }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => onCustomize(ADAPTIVE_INK)}
          aria-pressed={color === ADAPTIVE_INK}
          className={cn(
            "mt-2 flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[11px] transition-colors",
            color === ADAPTIVE_INK ? "bg-primary/15 text-primary" : chrome.hover,
          )}
        >
          <span
            className={cn("h-4 w-4 shrink-0 rounded-full ring-1", chrome.ring)}
            style={{ background: "linear-gradient(135deg, #1e1e1e 50%, #f4f4f5 50%)" }}
            aria-hidden
          />
          Adaptive — follows the paper
        </button>
        <label className="mt-2 flex items-center gap-2 text-[11px] opacity-70">
          <input
            type="color"
            value={color === ADAPTIVE_INK ? "#1e1e1e" : color}
            onChange={(e) => onCustomize(e.target.value)}
            aria-label={`Pick colour ${index + 1}`}
            className={cn(
              "h-6 w-6 shrink-0 cursor-pointer rounded ring-1 bg-transparent p-0",
              chrome.ring,
            )}
          />
          Custom…
        </label>
      </PopoverContent>
    </Popover>
  );
}
